import { useNodeConnections } from '@xyflow/react';

/**
 * Inline config field rendered inside a node body.
 * When `canBeWired` is true, this field has a corresponding TOP handle on the node.
 * If a data wire is connected to that handle, the input shows a "wired" indicator
 * instead of the editable control (the wire's value takes over at runtime).
 *
 * The actual Handle element is rendered by the parent node, not here.
 * This component only shows the wire indicator or the editable control.
 */
export const EcaInlineField = ({
  nodeId,
  fieldKey,
  label,
  value,
  type = 'text',
  options,
  placeholder,
  canBeWired = false,
  onChange,
}) => {
  const handleId = `data-in-${fieldKey}`;
  // Scoped subscription: re-renders only when THIS handle's connections change,
  // instead of on every edge-store mutation (the old useEdges() subscribed each
  // field to the full edges array → all fields re-rendered on any wire change).
  const connections = useNodeConnections({ id: nodeId, handleType: 'target', handleId });
  const isWired = canBeWired && connections.length > 0;

  return (
    <div className="flex items-center gap-1.5 min-h-[22px]">
      <span className="text-xs text-gray-400 w-24 shrink-0 truncate" title={label}>
        {label}
      </span>

      {isWired ? (
        <span className="text-xs bg-green-50 text-green-600 px-1.5 py-0.5 rounded border border-green-200 italic w-28 min-w-0">
          ← wired
        </span>
      ) : type === 'boolean' ? (
        <label className="flex items-center gap-1 cursor-pointer" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="w-3 h-3 accent-iris-600"
          />
          <span className="text-xs text-gray-500">{value ? 'on' : 'off'}</span>
        </label>
      ) : type === 'select' ? (
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          className="text-xs border border-gray-200 rounded px-1 py-0.5 w-28 min-w-0 bg-white text-gray-700"
        >
          {options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          value={value ?? ''}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          className="text-xs border border-gray-200 rounded px-1.5 py-0.5 w-28 min-w-0 text-gray-700 placeholder-gray-300 bg-white"
        />
      )}
    </div>
  );
};
